// frontend/src/lib/role-redirect.ts
// ───────────────────────────────────────────────────────────────────
// Page d'accueil par défaut selon le rôle. Utilisé après signin/signup et par
// la racine `/` pour un utilisateur déjà connecté (TASK-038 / SAUX-63).
//
// Les constantes de home sont exportées : elles étaient auparavant dupliquées en
// littéraux dans Signin.tsx et Signup.tsx, ce qui laissait la cible dériver.
// ───────────────────────────────────────────────────────────────────

import type { Role } from '@shared/types/auth';

/** Entrypoint tenantAdmin (TASK-022) : la liste de ses restaurants. */
export const TENANT_HOME = '/dashboard/restaurants';

/** Centre de pilotage superAdmin (SAUX-04/06 : redirige vers `tenants`). */
export const SUPER_ADMIN_HOME = '/admin/super';

/**
 * Racine publique. Sert de valeur « pas de home dédiée » : les appelants qui
 * redirigent NE DOIVENT PAS naviguer vers cette cible depuis `/` (boucle).
 */
export const PUBLIC_HOME = '/';

/**
 * Mappe un rôle vers sa page d'accueil par défaut.
 *
 * Le `fallback` ne concerne que les `tenantAdmin` (typiquement un `?redirect=`
 * sanitizé) ; les autres rôles ignorent le fallback car leur home est imposée
 * par leur périmètre fonctionnel.
 *
 * ⚠️ `restaurantManager` n'a **pas** de home dédiée aujourd'hui : la liste
 * `/dashboard/restaurants` est réservée au `tenantAdmin`, et un manager ne peut
 * entrer que sur un restaurant précis (`/dashboard/restaurants/:id`) — donc via
 * un id qu'une redirection générique ne connaît pas. On renvoie `PUBLIC_HOME`
 * (état connecté, sans cul-de-sac) plutôt qu'une route inexistante : l'ancienne
 * cible `/admin/my-restaurants` était un **404**. Une vraie home manager relève
 * du multi-manager (Phase 5) et n'est pas inventée ici.
 */
export function redirectToRoleHome(role: Role, fallback: string = TENANT_HOME): string {
  switch (role) {
    case 'superAdmin':
      return SUPER_ADMIN_HOME;
    case 'tenantAdmin':
      return fallback;
    case 'restaurantManager':
      return PUBLIC_HOME;
    case 'readonly':
      return PUBLIC_HOME;
  }
}

/**
 * `true` si le rôle a une home dédiée vers laquelle il est sûr de rediriger
 * depuis `/`. Empêche la boucle de redirection sur les rôles sans home.
 */
export function hasDedicatedHome(role: Role, fallback: string = TENANT_HOME): boolean {
  return redirectToRoleHome(role, fallback) !== PUBLIC_HOME;
}
